import React, {useEffect, useState} from "react";
import {styled} from "@mui/material/styles";
import {useField, useFormikContext} from "formik";
import {useDocumentTranslation} from "../documents/documentContext";
import {damsFetch} from "../app/damsFetch";
import Autocomplete from "@mui/material/Autocomplete";
import {Grid} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {getBrowserLocale} from "../utility";

const PREFIX = "InputLanguages";

const classes = {
    root: `${PREFIX}-root`,
    icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({theme}) => ({
    [`& .${classes.root}`]: {},

    [`& .${classes.icon}`]: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2),
    },
}));

/**
 * Renders an autocomplete input component for selecting languages.
 *
 * @param {string} formikKey - The key used for binding this component to a Formik field.
 * @param {string} label - The label to display for the input field.
 * @param {boolean} [disabled=false] - Whether the input component is disabled.
 * @param {string|null} [size=null] - The size of the input field.
 * @param {string|null} [helperText=null] - The helper text to display below the input field.
 * @returns {JSX.Element|null} The rendered autocomplete input component or null if not initialized.
 */
export const InputLanguages = ({
                                   formikKey,
                                   label,
                                   disabled = false,
                                   size = null,
                                   helperText = null,
                               }) => {
    const t = useDocumentTranslation();

    const [field, meta] = useField(formikKey);
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(true);
    const [languages, setLanguages] = useState([]);

    const {setFieldValue} = useFormikContext();

    const locale = getBrowserLocale(true);

    useEffect(() => {
        damsFetch("/languages", {method: "GET"}).then((json) => {
            setLanguages(json)
        });
    }, []);

    const languagesSearchHandler = (params) => {
        function capitalize(name) {
            return name.charAt(0).toUpperCase() + name.slice(1);
        }

        const localeArg = 'nb' ? 'no' : locale;

        const langs = [...languages]
            .filter(l => l.names[localeArg])
            .map(l => ({title: capitalize(l.names[localeArg]), value: l.uniqueId}));

        const matches = langs.filter(r => r.title.toLowerCase().includes(params.toLowerCase()));

        setOptions(matches);
        setSearching(false);
    };

    const changeHandler = (_event, values) => {
        setFieldValue(field.name, []).then();
        if (values.length > 0) {
            setFieldValue(field.name, values).then();
        }
    };

    const getValue = () => {
        return field.value;
    };

    if (undefined === meta.initialValue) {
        return null;
    } else {
        return (
            <Root>
                <Autocomplete
                    id="languages"
                    disabled={disabled}
                    multiple={true}
                    className={classes.root}
                    loading={Boolean(searching)}
                    loadingText={t("searchingPlaceholder", "Søker...")}
                    value={getValue()}
                    getOptionLabel={(option) => {
                        return option.title || '';
                    }}
                    options={options}
                    noOptionsText={t("txtNoMatches", "Ingen treff")}
                    onChange={changeHandler}
                    onInputChange={(_event, newInputValue) => {
                        languagesSearchHandler(newInputValue);
                    }}
                    renderInput={(params) => {
                        const {InputProps, inputProps: _ip, ...rest} = params;
                        return (
                            <TextField
                                {...rest}
                                label={label}
                                size={size}
                                helperText={helperText}
                                slotProps={{
                                    ...params.slotProps,
                                    input: {...(params.slotProps?.input ?? InputProps ?? {})}
                                }}
                            />
                        );
                    }}
                    renderOption={({key, ...props}, option, _state) => {
                        // Use option.value (uniqueId) to guarantee a unique key even when
                        // two options share the same title (e.g. "Vietnamesisk").
                        const uniqueKey = option.value ? `${key}-${option.value}` : key;
                        return (
                            <Grid
                                container
                                {...props}
                                key={uniqueKey}
                                sx={[{
                                    alignItems: "center"
                                }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>
                                <Grid size="grow">
                                    <Typography variant="body2" color="textSecondary">
                                        {option.title}
                                    </Typography>
                                </Grid>
                            </Grid>
                        );
                    }}
                />
            </Root>
        );
    }
};
